<script lang="ts">
  import type { FormComponentSlotProps } from "@inertiajs/core"
  import { Form } from "@inertiajs/svelte"
  import { LoaderCircle } from "@lucide/svelte"

  import InputError from "@/components/input-error.svelte"
  import { Button } from "@/components/ui/button"
  import { Input } from "@/components/ui/input"
  import { Label } from "@/components/ui/label"
  import AppLayout from "@/layouts/app-layout.svelte"
  import { newUserPath, usersPath } from "@/routes"
  import type { BreadcrumbItem } from "@/types"

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Users",
      href: usersPath(),
    },
    {
      title: "New User",
      href: newUserPath(),
    },
  ]
</script>

<svelte:head>
  <title>New User - Chorizo</title>
</svelte:head>

<AppLayout {breadcrumbs}>
  <div class="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-4">
    <div>
      <h1 class="text-3xl font-bold">Add New User</h1>
      <p class="text-muted-foreground mt-2">Create a new user for your group</p>
    </div>

    <div class="max-w-2xl">
      <Form method="post" action={usersPath()} class="space-y-6">
        {#snippet children({ processing, errors }: FormComponentSlotProps)}
          <div class="space-y-4">
            <div class="space-y-2">
              <Label for="name">Name</Label>
              <Input
                id="name"
                name="name"
                type="text"
                required
                autofocus
                placeholder="User name"
              />
              <InputError message={errors.name} />
            </div>

            <div class="space-y-2">
              <Label for="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="Leave blank to auto-generate"
              />
              <p class="text-muted-foreground text-sm">
                Leave blank to auto-generate an email, or enter a custom email
              </p>
              <InputError message={errors.email} />
            </div>

            <div class="space-y-2">
              <Label for="role">Role</Label>
              <select
                id="role"
                name="role"
                required
                class="border-input placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
              <InputError message={errors.role} />
            </div>

            <div class="space-y-2">
              <Label for="password">PIN/Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="Leave blank to auto-generate"
                minlength={4}
              />
              <p class="text-muted-foreground text-sm">
                Leave blank to auto-generate a PIN, or enter a custom
                PIN/password (minimum 4 characters)
              </p>
              <InputError message={errors.password} />
            </div>

            <div class="flex gap-4">
              <Button type="submit" disabled={processing}>
                {#if processing}
                  <LoaderCircle class="h-4 w-4 animate-spin" />
                {/if}
                Create User
              </Button>
              <Button type="button" variant="outline" href={usersPath()}>
                Cancel
              </Button>
            </div>
          </div>
        {/snippet}
      </Form>
    </div>
  </div>
</AppLayout>
